"use client";

/**
 * PDF Viewer Component
 *
 * Uses pdfjs-dist to render PDF documents with citation text highlighting.
 *
 * ## PDF.js Worker Configuration
 *
 * The PDF.js library requires a web worker for parsing PDFs. The worker file
 * is kept in sync with the pdfjs-dist version automatically:
 *
 * 1. `postinstall` script copies the worker after `npm install`
 * 2. `prebuild` script copies the worker before each build
 *
 * This ensures the worker version always matches the installed pdfjs-dist.
 *
 * ### When updating pdfjs-dist:
 * - Run `npm install` (postinstall handles the copy)
 * - Or run `npm run build` (prebuild handles the copy)
 * - The worker at `/public/pdf.worker.min.mjs` will be updated automatically
 *
 * @see package.json for the postinstall and prebuild scripts
 */

import React from "react";
// PINNED to 5.4.624 — do not upgrade past 5.4.x until Map.getOrInsertComputed
// has broad browser support. pdfjs-dist 5.5.207+ uses this TC39 Stage 4 API
// which is missing in Safari, Firefox, and some Chromium builds. Upgrading
// causes silent render failures (blank canvas, no error in UI).
// Revisit after Safari 19+ and Firefox 140+ ship with support (~Q3 2026).
import * as pdfjsLib from "pdfjs-dist";
import { Button, Spinner, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { PDFDocumentProxy, TextItem } from "pdfjs-dist/types/src/display/api";

import { findTextMatch, normalizeText, MatchResult } from "@/utils/textMatching";
import { boundedExtendItems } from "@/utils/citations/boundedExtend";
import { logger } from "@/telemetry";

// Configure PDF.js worker for version 5.x
// The worker file is copied to public/ folder via postinstall/prebuild scripts
// This avoids external CDN dependency and ensures version compatibility
if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

/**
 * Scale the text layer coordinates are baked at.
 *
 * Highlight rectangles are derived from `textLayer`, whose x, y, width and height
 * are multiplied by this figure once during extraction. Rather than re-running the
 * text matching whenever the zoom changes, the highlight overlay is sized at this
 * scale and then transformed to the live scale, so the two stay registered against
 * the canvas at any zoom.
 *
 * Extraction and the overlay are a matched pair, being the sizing and the transform
 * together, and changing how one of them uses this figure without the other silently
 * unregisters every highlight from the page beneath it. The one other reference is
 * the fallback in `scale` below, which is independent of the pair and is about a
 * container that cannot be measured rather than about the text layer.
 */
const REFERENCE_SCALE = 1.5;

/** Closest and furthest the manual zoom controls will go, as a canvas scale factor. */
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;

/** Added or removed by one press of zoom in or zoom out. */
const SCALE_STEP = 0.25;

/** Turns a fractional scale (1.25) into a whole percentage for display (125). */
const ZOOM_PERCENTAGE_MULTIPLIER = 100;

/**
 * Horizontal padding inside the scroll container, being `p-4` on both sides, which
 * fit-to-width has to leave room for or the fitted page overflows by that amount and
 * reintroduces the horizontal scrollbar it exists to avoid.
 */
const CONTAINER_PADDING_PX = 32;

/**
 * pdfjs reports a superseded or aborted render by name rather than by error type, so
 * the name is all there is to match on.
 */
const isRenderCancellation = (error: unknown): boolean => {
  const name = (error as { name?: string } | null | undefined)?.name;

  return name === "RenderingCancelledException" || name === "AbortException";
};

/** Keeps a scale inside the bounds the controls offer. */
const clampScale = (value: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

export interface PDFViewerProps {
  /** PDF document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the PDF */
  citationText?: string;

  /** Callback when text is found or not found */
  onMatchResult?: (result: MatchResult | null) => void;
}

interface TextLayerItem {
  text: string;
  transform: number[];
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * PDF Viewer component using PDF.js
 * Renders PDF pages and highlights citation text
 */
export const PDFViewer: React.FC<PDFViewerProps> = ({
  documentData,
  citationText,
  onMatchResult,
}) => {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = React.useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = React.useState(0);
  /** Live width of the scroll container, tracked so fit-to-width survives a resize. */
  const [containerWidth, setContainerWidth] = React.useState(0);
  /**
   * Whether the container has been measured at all, as distinct from having been
   * measured as zero. The render pass waits on this rather than on a usable width, so
   * a container that cannot be measured still renders at the fallback scale.
   */
  const [hasMeasured, setHasMeasured] = React.useState(false);
  /**
   * Width in points of the widest page in the document, measured at scale 1. The
   * widest rather than the first, so that a landscape page in an otherwise portrait
   * document does not overflow once the rest has been fitted.
   */
  const [widestPageWidth, setWidestPageWidth] = React.useState(0);
  /**
   * A scale the user chose explicitly, which pins the zoom. Null means the zoom
   * follows fit-to-width, which is the default and what a resize recalculates.
   */
  const [userScale, setUserScale] = React.useState<number | null>(null);
  /**
   * Page the view has already been scrolled to. Consulted so that re-running the
   * highlight effect after a zoom redraws the overlay without yanking the view back
   * to the citation the user may have scrolled away from.
   */
  const lastScrolledPageRef = React.useRef<number | null>(null);
  const [textLayer, setTextLayer] = React.useState<TextLayerItem[]>([]);
  const [matchedPage, setMatchedPage] = React.useState<number | null>(null);
  const canvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());
  /**
   * Incremented once every page has finished rendering. The highlight effect depends
   * on it so that it redraws after the pages it draws onto actually exist.
   *
   * Pages render sequentially, each awaiting the previous page's raster, so on a long
   * document the fixed 300ms delay below expires long before the matched page has been
   * appended. The effect then found no element, logged that pages were still rendering
   * and never retried, so a citation past the first few pages lost its highlight on
   * every zoom press. The delay is kept as the fast path for short documents, and this
   * counter is the one that guarantees a redraw on long ones.
   */
  const [renderVersion, setRenderVersion] = React.useState(0);
  const [pageHighlights, setPageHighlights] = React.useState<
    Record<number, Array<{ left: number; top: number; width: number; height: number }>>
  >({});

  /**
   * Scale at which the widest page exactly fills the available width, or zero where
   * either measurement is still missing.
   */
  const fitScale =
    containerWidth > 0 && widestPageWidth > 0
      ? clampScale((containerWidth - CONTAINER_PADDING_PX) / widestPageWidth)
      : 0;

  /**
   * The scale actually rendered, being the user's pinned choice where they have made
   * one, then fit-to-width, then REFERENCE_SCALE. That last fallback matters, because
   * a container whose width cannot be read yields a fit scale of zero, and a viewer
   * that renders nothing at all would be a worse failure than one rendering unfitted.
   */
  const scale = userScale ?? (fitScale > 0 ? fitScale : REFERENCE_SCALE);

  /** True while the zoom is following the container rather than a pinned choice. */
  const isFitToWidth = userScale === null;

  /**
   * Track the scroll container's width so fit-to-width responds to the modal being
   * resized, the window changing, or the sidebar opening.
   *
   * A layout effect rather than a passive one, so the width is known before the
   * browser paints and pages are drawn once at the fitted scale instead of appearing
   * at REFERENCE_SCALE and jumping. `hasMeasured` is what the render effect waits on,
   * being set even when the width reads as zero, so an unmeasurable container still
   * renders at the fallback rather than staying blank.
   *
   * Keyed on `pdfDocument` because the container is only mounted once loading has
   * finished, so an effect running on mount alone would find no element to observe.
   */
  React.useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    setContainerWidth(element.clientWidth);
    setHasMeasured(true);

    if (typeof ResizeObserver === "undefined") {
      // The measurement above has already run, so fit-to-width still works and only
      // its response to a resize is lost. Logged because that degradation is otherwise
      // invisible, and would be reported as "fit to width is broken" with nothing in
      // telemetry to point at.
      logger.warn("ResizeObserver unavailable, PDF fit-to-width will not follow resizes");

      return;
    }

    // `clientWidth` rather than the entry's `contentRect`, so both this and the
    // initial measurement above report the same box. `contentRect` is the content
    // box and has already excluded the container's padding, so feeding it through
    // the same `CONTAINER_PADDING_PX` subtraction deducted that padding a second
    // time and left every page fitted 32px narrower than the space available.
    // `clientWidth` is also what every other measurement site in the app reads.
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth((entry.target as HTMLElement).clientWidth);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [pdfDocument]);

  // Both handlers step from `scale`, the figure actually rendered, rather than from
  // `fitScale`. The two agree while fit-to-width is working, but `fitScale` is zero
  // where the container could not be measured and `scale` has fallen back to
  // REFERENCE_SCALE, and stepping from zero there clamped to MIN_SCALE in either
  // direction, so zoom in dropped the view from 150% to 25% and zoom out produced
  // that same value.
  const handleZoomIn = React.useCallback(() => {
    setUserScale((current) => clampScale((current ?? scale) + SCALE_STEP));
  }, [scale]);

  const handleZoomOut = React.useCallback(() => {
    setUserScale((current) => clampScale((current ?? scale) - SCALE_STEP));
  }, [scale]);

  const handleFitToWidth = React.useCallback(() => {
    setUserScale(null);
  }, []);

  // Load PDF document
  React.useEffect(() => {
    let isMounted = true;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        // A different document opens fitted to the container and unscrolled, rather
        // than inheriting the zoom and scroll position of the one before it.
        setUserScale(null);
        setWidestPageWidth(0);
        lastScrolledPageRef.current = null;

        loadingTask = pdfjsLib.getDocument({ data: documentData });

        const pdf = await loadingTask.promise;

        if (!isMounted) return;

        // Measure every page at scale 1 so fit-to-width can size against the widest of
        // them rather than the first, which is what stops a landscape page in an
        // otherwise portrait document overflowing once the rest has been fitted.
        // `getPage` caches inside pdfjs, so the render and text extraction passes that
        // follow reuse these pages rather than fetching each one again. Measuring is
        // concurrent because each page is an independent worker round trip, and it has
        // to finish before first paint, or pages would be drawn at the fallback scale
        // and then jump once the fitted scale arrived.
        //
        // `allSettled` rather than `all`, because `all` rejects on the first rejection
        // and sent a single malformed page dictionary to the outer catch, replacing a
        // perfectly readable report with "Failed to load PDF document". A page that
        // cannot be measured now costs only its own contribution to the widest figure,
        // and a document where none can be measured falls back to REFERENCE_SCALE.
        const pageNumbers = Array.from({ length: pdf.numPages }, (_unused, index) => index + 1);
        const measurements = await Promise.allSettled(
          pageNumbers.map(async (pageNum) => {
            const page = await pdf.getPage(pageNum);

            return page.getViewport({ scale: 1 }).width;
          }),
        );
        const unmeasurable = pageNumbers.filter(
          (_unused, index) => measurements[index].status === "rejected",
        );

        if (unmeasurable.length > 0) {
          const firstRejection = measurements.find(
            (result): result is PromiseRejectedResult => result.status === "rejected",
          );

          logger.error("Failed to measure some PDF pages", firstRejection?.reason, {
            failedPages: unmeasurable.join(","),
            totalPages: pdf.numPages,
          });
        }

        const widest = measurements.reduce(
          (max, result) => (result.status === "fulfilled" ? Math.max(max, result.value) : max),
          0,
        );

        if (!isMounted) return;

        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
        setWidestPageWidth(widest);
        logger.info("📄 PDF loaded", { numPages: pdf.numPages, widestPageWidth: widest });
      } catch (err) {
        logger.error("Failed to load PDF", err);
        if (isMounted) {
          setError(t("citations.pdfViewer.loadError"));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
      // Release the worker port and the page cache. Nothing destroyed the document
      // before, so each citation opened in a session left both behind, and because the
      // measurement above pulls every page into that cache the residue is now the
      // whole document rather than only the pages the reader reached. Renders still in
      // flight reject with an abort, which the render loop logs at debug.
      loadingTask?.destroy().catch((err) => {
        logger.error("Failed to destroy PDF loading task", err);
      });
    };
    // `t` is deliberately omitted. It is stable in production, but the global
    // `useTranslation` mock in src/setupTests.ts returns a fresh function on every
    // render, so depending on it here reloads the document in a loop and takes the
    // vitest worker down with it. See the testing notes in CLAUDE.md.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  // Extract text layer from all pages
  React.useEffect(() => {
    if (!pdfDocument) return;

    let isMounted = true;

    const extractTextLayer = async () => {
      try {
        const items: TextLayerItem[] = [];

        // IMPORTANT: these coordinates are baked at REFERENCE_SCALE, not at the live
        // zoom. The overlay that draws them is transformed from this scale to the
        // rendered one, so extraction never has to re-run when the user zooms.
        const scale = REFERENCE_SCALE;

        for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
          const page = await pdfDocument.getPage(pageNum);
          const textContent = await page.getTextContent();
          const viewport = page.getViewport({ scale });

          textContent.items.forEach((item) => {
            if ("str" in item) {
              const textItem = item as TextItem;
              const transform = textItem.transform;

              // Calculate position from transform matrix
              // transform is [scaleX, skewY, skewX, scaleY, translateX, translateY]
              // These coordinates are at the same scale as the canvas
              const x = transform[4] * scale;
              const y = viewport.height - transform[5] * scale; // Flip Y coordinate
              const width = textItem.width * scale;
              const height = textItem.height * scale;

              items.push({
                text: textItem.str,
                transform,
                page: pageNum,
                x,
                y,
                width,
                height,
              });
            }
          });
        }

        if (isMounted) {
          setTextLayer(items);
          logger.info("📝 Extracted text from PDF pages", {
            numPages: pdfDocument.numPages,
            itemCount: items.length,
          });
        }
      } catch (err) {
        logger.error("Failed to extract text layer", err);
      }
    };

    extractTextLayer();

    return () => {
      isMounted = false;
    };
  }, [pdfDocument]);

  // Find citation text in extracted text layer
  React.useEffect(() => {
    if (!citationText || textLayer.length === 0) return;

    const documentText = textLayer.map((item) => item.text).join(" ");

    logger.info("🔍 Looking for citation", {
      citationPreview: citationText.substring(0, 100),
      normalizedPreview: normalizeText(citationText).substring(0, 100),
    });

    const matchResult = findTextMatch(documentText, citationText);

    logger.info("🔍 Citation match result", { found: String(!!matchResult) });
    if (matchResult) {
      logger.info("🔍 Found text", {
        matchedTextPreview: matchResult.matchedText.substring(0, 100),
        method: matchResult.method,
        confidence: Math.round(matchResult.confidence * 100) + "%",
      });
    }

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    // Find start and end across pages and compute cross-page highlights
    if (matchResult) {
      logger.info("🔍 Starting start/end page detection using head/tail segments");

      // Build ordered page list and page texts
      const pageNumbers = Array.from(new Set(textLayer.map((item) => item.page))).sort(
        (a, b) => a - b,
      );

      const getPageTextItems = (p: number) => textLayer.filter((i) => i.page === p);
      const getPageText = (p: number) =>
        getPageTextItems(p)
          .map((i) => i.text)
          .join(" ");

      // Head and tail segments from the citation
      const headLen = Math.min(100, citationText.length);
      const headSeg = citationText.substring(0, headLen);
      const tailLen = Math.min(100, citationText.length);
      const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

      logger.info("🔍 Head/tail segments", {
        headPreview: headSeg.substring(0, 80),
        tailPreview: tailSeg.substring(0, 80),
      });

      // Find best page for head
      let startPage: number | null = null;
      let startConf = 0;

      for (const p of pageNumbers) {
        const pageText = getPageText(p);
        const r = findTextMatch(pageText, headSeg);

        if (r && r.confidence > startConf) {
          startConf = r.confidence;
          startPage = p;
        }
      }
      if (startPage) {
        logger.info("📍 Start page found", {
          startPage,
          confidence: Math.round(startConf * 100) + "%",
        });
      } else {
        logger.info(
          "⚠️ Could not find start page via head segment; falling back to earlier method",
        );
      }

      // Find best page for tail.
      //
      // First check: if the full citation fits on startPage, the citation
      // does not span pages — force endPage = startPage and skip the
      // tail-page search entirely. This avoids the cross-page fan-out
      // observed on NG_ET_BP_048 where short common phrases at the end of
      // a chunk ("See Section 3", "Fire Safety", "competent person", etc.)
      // appear as cross-references on multiple pages, causing the tail to
      // pick a later page and the highlight to span every page in between.
      //
      // Constraint on the fallback search: tail page must be at or after
      // startPage. A citation cannot legitimately have its tail before its
      // head in reading order. When startPage is unknown, the constraint
      // collapses to the original any-page search.
      let endPage: number | null = null;
      let endConf = 0;
      const minEndPage = startPage ?? Number.NEGATIVE_INFINITY;

      if (startPage) {
        const startPageText = getPageText(startPage);
        const fullOnStartPage = findTextMatch(startPageText, citationText);

        if (fullOnStartPage && fullOnStartPage.confidence >= 0.8) {
          endPage = startPage;
          endConf = fullOnStartPage.confidence;
          logger.info("📍 Citation fits on startPage; skipping tail-page search", {
            startPage,
            confidence: Math.round(endConf * 100) + "%",
          });
        }
      }

      if (!endPage) {
        for (const p of pageNumbers) {
          if (p < minEndPage) continue;
          const pageText = getPageText(p);
          const r = findTextMatch(pageText, tailSeg);

          if (r && r.confidence > endConf) {
            endConf = r.confidence;
            endPage = p;
          }
        }
      }
      if (endPage) {
        logger.info("📍 End page found", { endPage, confidence: Math.round(endConf * 100) + "%" });
      } else {
        logger.info("⚠️ Could not find end page via tail segment (fallback will handle)");
      }

      // Fallback: if no start page determined, try previous page-detection approach (closest match to full citation)
      if (!startPage) {
        let fallbackPage: number | null = null;
        const matchedTextStart = matchResult.matchedText.substring(0, 80);

        for (const p of pageNumbers) {
          const pageText = getPageText(p);

          if (pageText.includes(matchedTextStart)) {
            fallbackPage = p;
            break;
          }
        }
        if (!fallbackPage) {
          // try normalized search
          for (const p of pageNumbers) {
            const pageText = getPageText(p);
            const r = findTextMatch(pageText, citationText);

            if (r && r.confidence >= 0.7) {
              fallbackPage = p;
              break;
            }
          }
        }
        startPage = fallbackPage;
        if (startPage) logger.info("📍 Fallback start page", { startPage });
      }

      if (startPage) {
        // Helper to build normalized page text and per-item start/length offsets
        const buildNormPage = (items: TextLayerItem[]) => {
          const starts: number[] = [];
          const lens: number[] = [];
          const charToItemIndex: number[] = [];
          let text = "";

          const isWordChar = (ch: string) => /[\p{L}\p{N}]/u.test(ch);

          for (let idx = 0; idx < items.length; idx++) {
            const it = items[idx];
            const curr = normalizeText(it.text);
            const prev = idx > 0 ? normalizeText(items[idx - 1].text) : "";

            // Decide whether to insert a space between items.
            // Insert space only if the previous item ends with a word char, the current starts with a word char,
            // and the previous does NOT end with a hyphen (common hyphenation case).
            const prevLast = prev.slice(-1);
            const currFirst = curr.slice(0, 1);
            const shouldInsertSpace =
              idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

            if (shouldInsertSpace) {
              text += " ";
              charToItemIndex.push(-1); // separator, belongs to no item
            }

            const start = text.length;

            starts.push(start);

            // Append current normalized text and map each character to this item index
            for (let k = 0; k < curr.length; k++) {
              text += curr[k];
              charToItemIndex.push(idx);
            }

            lens.push(curr.length);
          }

          return { text, starts, lens, charToItemIndex };
        };

        // Determine item index on start page using normalized head match position
        const startItems = getPageTextItems(startPage);
        const normStartPage = buildNormPage(startItems);
        const headMatchOnStart = findTextMatch(normStartPage.text, headSeg);
        let startIndex = 0;

        if (headMatchOnStart) {
          let pos = headMatchOnStart.position;

          // Clamp to valid range
          pos = Math.max(0, Math.min(pos, normStartPage.text.length - 1));
          // Prefer the exact char mapping; if we land on a separator (-1), scan to nearest item char
          const nearestForward = () => {
            for (let p = pos; p < normStartPage.charToItemIndex.length; p++) {
              const idx = normStartPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          const nearestBackward = () => {
            for (let p = pos; p >= 0; p--) {
              const idx = normStartPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          let idx = normStartPage.charToItemIndex[pos];

          if (idx < 0) {
            const f = nearestForward();

            idx = f >= 0 ? f : nearestBackward();
          }
          if (idx >= 0) {
            startIndex = idx;
            logger.info("🎯 Start position mapped via char map", {
              position: pos,
              itemIndex: idx,
              page: startPage,
            });
          }
        } else {
          // Fallback to first-token heuristic
          const headNorm = normalizeText(headSeg);
          const headTokens = headNorm.split(/\s+/).filter(Boolean);
          const firstToken = headTokens[0] || "";

          for (let i = 0; i < startItems.length; i++) {
            if (startItems[i].text.toLowerCase().includes(firstToken)) {
              startIndex = i;
              logger.info("🎯 Start token found via fallback", {
                firstToken,
                itemIndex: i,
                page: startPage,
              });
              break;
            }
          }
        }

        let resolvedEndPage = endPage || startPage;
        const endItems = getPageTextItems(resolvedEndPage);
        const normEndPage = buildNormPage(endItems);
        // When the citation lives on one page, anchor endIndex to the end
        // of the FULL citation match — not a tail-segment match. The tail
        // is short and prone to matching repeated phrases later on the
        // page (e.g. citation tail "See Section 3" also appearing in
        // "section 3.2" further down), which causes intra-page
        // over-extension. The full citation has enough context to land at
        // the actual end of the cited content.
        const tailMatchOnEnd =
          resolvedEndPage === startPage
            ? (findTextMatch(normEndPage.text, citationText) ??
              findTextMatch(normEndPage.text, tailSeg))
            : findTextMatch(normEndPage.text, tailSeg);
        let endIndex = endItems.length - 1;

        if (tailMatchOnEnd) {
          let endPos = tailMatchOnEnd.position + tailMatchOnEnd.length - 1;

          endPos = Math.max(0, Math.min(endPos, normEndPage.text.length - 1));

          const nearestBackward = () => {
            for (let p = endPos; p >= 0; p--) {
              const idx = normEndPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          const nearestForward = () => {
            for (let p = endPos; p < normEndPage.charToItemIndex.length; p++) {
              const idx = normEndPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };

          // Prefer the item that actually contains the last matched character (backward bias)
          let idx = normEndPage.charToItemIndex[endPos];

          if (idx < 0) {
            // If we landed on a separator, choose the closest item char; prefer backward to avoid overshooting
            const b = nearestBackward();

            idx = b >= 0 ? b : nearestForward();
          }

          if (idx >= 0) {
            endIndex = idx;
            logger.info("🎯 End position mapped via char map", {
              position: endPos,
              itemIndex: idx,
              page: resolvedEndPage,
            });
          }
        } else {
          // Fallback to last-token heuristic
          const tailNorm = normalizeText(tailSeg);
          const tailTokens = tailNorm.split(/\s+/).filter(Boolean);
          const lastToken = tailTokens[tailTokens.length - 1] || "";

          for (let i = endItems.length - 1; i >= 0; i--) {
            if (endItems[i].text.toLowerCase().includes(lastToken)) {
              endIndex = i;
              logger.info("🎯 End token found via fallback", {
                lastToken,
                itemIndex: i,
                page: resolvedEndPage,
              });
              break;
            }
          }
        }

        // Build cross-page highlight plan
        const plan: Record<
          number,
          Array<{ left: number; top: number; width: number; height: number }>
        > = {};
        const fromPage = Math.min(startPage, resolvedEndPage);
        const toPage = Math.max(startPage, resolvedEndPage);
        let totalBoxes = 0;

        // Same-page case: use the strict-bounded walk to determine which
        // items genuinely belong to the chunk. The walk stops as soon as it
        // sees an item that isn't in the chunk text, so unrelated content
        // on the same page (other bullets, page footer, other sections) is
        // never highlighted. See src/utils/citations/boundedExtend.ts.
        if (startPage === resolvedEndPage) {
          const items = getPageTextItems(startPage);
          const chunkNorm = normalizeText(citationText);
          const highlightedIndices = boundedExtendItems(items, startIndex, chunkNorm);

          for (const i of highlightedIndices) {
            const it = items[i];
            const top = it.y - it.height * 0.85;

            plan[startPage] = plan[startPage] || [];
            plan[startPage].push({ left: it.x, top, width: it.width, height: it.height });
            totalBoxes++;
          }
        } else {
          // Cross-page case: keep the existing contiguous-range logic.
          // boundedExtendItems is same-page only by design; for chunks
          // that genuinely span pages we fall back to the index-range
          // approach.
          for (let p = fromPage; p <= toPage; p++) {
            const items = getPageTextItems(p);
            let fromIdx = 0;
            let toIdx = items.length - 1;

            if (p === startPage) {
              fromIdx = Math.max(0, Math.min(startIndex, items.length - 1));
            } else if (p === resolvedEndPage) {
              toIdx = Math.max(0, Math.min(endIndex, items.length - 1));
            }

            for (let i = fromIdx; i <= toIdx; i++) {
              const it = items[i];
              const top = it.y - it.height * 0.85;

              plan[p] = plan[p] || [];
              plan[p].push({ left: it.x, top, width: it.width, height: it.height });
              totalBoxes++;
            }
          }
        }

        setPageHighlights(plan);
        setMatchedPage(startPage);
        logger.info("🎨 Cross-page highlight planned", {
          fromPage,
          toPage,
          totalBoxes,
        });
      } else {
        logger.warn("⚠️ Could not determine start page; skipping highlight plan generation");
      }
    }
  }, [citationText, textLayer, onMatchResult]);

  // Render PDF pages. Re-runs on a zoom, because the canvas is a raster and scaling
  // it in CSS would blur the text rather than sharpen it.
  React.useEffect(() => {
    if (!pdfDocument || !containerRef.current) return;

    // Wait for the container to have been measured before drawing anything, so pages
    // are not rendered at the fallback scale and immediately redrawn at the fitted one.
    if (!hasMeasured) return;

    let isMounted = true;
    const renderTasks: Array<{ cancel: () => void }> = [];

    const renderPages = async () => {
      const container = containerRef.current;

      if (!container) return;

      // Clear existing canvases
      container.innerHTML = "";
      canvasRefs.current.clear();
      highlightRefs.current.clear();

      for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
        if (!isMounted) return;

        try {
          const page = await pdfDocument.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          // Create page container
          const pageDiv = document.createElement("div");

          pageDiv.className = "relative mb-4 shadow-medium";
          pageDiv.style.width = `${viewport.width}px`;
          pageDiv.style.height = `${viewport.height}px`;

          // Create canvas for PDF rendering
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) {
            // The page still has to join the DOM. `container.children` is indexed
            // positionally by the scroll target below, so omitting the append shifted
            // every later page down one place and sent a citation to the wrong page
            // while highlighting the right one, with the two disagreeing silently. A
            // browser returns null here once its canvas memory ceiling is passed,
            // which the 400% zoom ceiling puts within reach on a long document.
            logger.error("Could not acquire a 2D context for a PDF page", undefined, {
              pageNum,
              scale,
              totalPages: pdfDocument.numPages,
            });
            container.appendChild(pageDiv);
            continue;
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "block";

          // Create highlight layer. It is sized at REFERENCE_SCALE, because that is
          // the scale the rectangles in `textLayer` and `pageHighlights` were computed
          // at, and then transformed onto the live canvas. Transforming the whole
          // overlay is exact and keeps the text matching independent of the zoom,
          // where re-deriving every rectangle per zoom step would not be.
          const referenceViewport = page.getViewport({ scale: REFERENCE_SCALE });
          const highlightLayer = document.createElement("div");

          highlightLayer.className = "absolute top-0 left-0 pointer-events-none";
          highlightLayer.style.width = `${referenceViewport.width}px`;
          highlightLayer.style.height = `${referenceViewport.height}px`;
          highlightLayer.style.transformOrigin = "top left";
          highlightLayer.style.transform = `scale(${scale / REFERENCE_SCALE})`;

          pageDiv.appendChild(canvas);
          pageDiv.appendChild(highlightLayer);
          container.appendChild(pageDiv);

          canvasRefs.current.set(pageNum, canvas);
          highlightRefs.current.set(pageNum, highlightLayer);

          // Render PDF page
          const renderTask = page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas,
          });

          renderTasks.push(renderTask);
          await renderTask.promise;

          logger.info("✅ Rendered page", { pageNum });
        } catch (err) {
          // A zoom press supersedes whatever is drawing, so a cancellation here is the
          // mechanism working rather than a fault. Reporting it at error severity
          // filled telemetry with cancellations from ordinary zooming and buried the
          // genuine corruption this branch exists to surface.
          if (isRenderCancellation(err)) {
            logger.debug("Superseded page render cancelled", { pageNum });
          } else {
            logger.error(`Failed to render page ${pageNum}`, err, { pageNum, scale });
          }
        }
      }

      if (isMounted) setRenderVersion((version) => version + 1);
    };

    renderPages();

    return () => {
      isMounted = false;
      // Stop whatever is still drawing. `isMounted` only stops the loop advancing to
      // the next page, so without this a rapid series of zoom presses left overlapping
      // full-document passes running against canvases already detached from the
      // container, competing for the same worker.
      for (const task of renderTasks) task.cancel();
    };
  }, [pdfDocument, scale, hasMeasured]);

  // Scroll to matched page and add highlight (including cross-page plan).
  // Depends on `scale` because a zoom rebuilds every page, discarding the overlay
  // children this effect appended, so the highlights have to be drawn again, and on
  // `renderVersion` so that the redraw happens once those pages exist rather than
  // whenever the fixed delay below happens to expire.
  React.useEffect(() => {
    if (matchedPage === null || !containerRef.current) return;

    // Wait for pages to be rendered before scrolling
    // Use a small delay to ensure DOM is ready
    const scrollTimeout = setTimeout(() => {
      const container = containerRef.current;

      if (!container) return;

      const pageElements = container.children;
      const pageElement = pageElements[matchedPage - 1] as HTMLElement;

      if (pageElement) {
        // Only scroll the first time a given page is matched. A zoom re-runs this
        // effect to redraw the overlay, and scrolling again would drag the user back
        // to the citation every time they pressed zoom in.
        if (lastScrolledPageRef.current !== matchedPage) {
          logger.info("📜 Scrolling to page", { matchedPage });
          pageElement.scrollIntoView({ behavior: "smooth", block: "center" });
          lastScrolledPageRef.current = matchedPage;
        }

        // If we have a cross-page plan, apply it to all pages involved
        const pages = Object.keys(pageHighlights).map((n) => parseInt(n, 10));

        if (pages.length > 0) {
          logger.info("🎨 Applying cross-page highlights", { pages: pages.join(", ") });
          for (const p of pages) {
            const layer = highlightRefs.current.get(p);

            if (!layer) continue;
            layer.innerHTML = "";
            const rects = pageHighlights[p] || [];

            for (const r of rects) {
              const hl = document.createElement("div");

              hl.className =
                "absolute bg-warning/30 dark:bg-warning/40 border border-warning rounded";
              hl.style.left = `${r.left}px`;
              hl.style.top = `${r.top}px`;
              hl.style.width = `${r.width}px`;
              hl.style.height = `${r.height}px`;
              hl.style.pointerEvents = "none";
              layer.appendChild(hl);
            }
            logger.info("🎨 Added highlights on page", { highlightCount: rects.length, page: p });
          }
        } else if (citationText) {
          // Fallback: single-page highlight (legacy behavior)
          const highlightLayer = highlightRefs.current.get(matchedPage);

          if (highlightLayer) {
            highlightLayer.innerHTML = "";
            const pageTextItems = textLayer.filter((item) => item.page === matchedPage);
            const pageText = pageTextItems.map((item) => item.text).join(" ");
            const matchResult = findTextMatch(pageText, citationText);

            if (matchResult) {
              const words = matchResult.matchedText.trim().split(/\s+/);
              const firstWord = words[0].toLowerCase();
              const lastWord = words[words.length - 1].toLowerCase();
              let firstIndex = -1;
              let lastIndex = -1;
              let foundFirst = false;

              pageTextItems.forEach((item, index) => {
                const itemTextLower = item.text.toLowerCase();

                if (!foundFirst && itemTextLower.includes(firstWord)) {
                  firstIndex = index;
                  foundFirst = true;
                }
                if (foundFirst && itemTextLower.includes(lastWord)) {
                  lastIndex = index;
                }
              });
              if (firstIndex !== -1 && lastIndex !== -1) {
                for (let i = firstIndex; i <= lastIndex; i++) {
                  const item = pageTextItems[i];
                  const highlight = document.createElement("div");
                  const top = item.y - item.height * 0.85;

                  highlight.className =
                    "absolute bg-warning/30 dark:bg-warning/40 border border-warning rounded";
                  highlight.style.left = `${item.x}px`;
                  highlight.style.top = `${top}px`;
                  highlight.style.width = `${item.width}px`;
                  highlight.style.height = `${item.height}px`;
                  highlight.style.pointerEvents = "none";
                  highlightLayer.appendChild(highlight);
                }
                logger.info("🎨 Added highlights on page (fallback)", {
                  highlightCount: lastIndex - firstIndex + 1,
                  page: matchedPage,
                });
              }
            }
          }
        }
      } else {
        logger.info("⚠️ Page element not found (may still be rendering)", { matchedPage });
      }
    }, 300); // 300ms delay to ensure pages are rendered

    return () => clearTimeout(scrollTimeout);
  }, [matchedPage, citationText, textLayer, pageHighlights, scale, renderVersion]);

  // The caption is a sibling rather than a `label` prop. HeroUI v3's Spinner declares
  // only className, colour and size, so a `label` was forwarded to the underlying span
  // as a stray DOM attribute, rendering no text and leaving this status region with
  // nothing to announce.
  if (loading) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3" role="status">
        <Spinner />
        <p className="text-sm text-default-500">{t("citations.pdfViewer.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[60vh] items-center justify-center" role="alert">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  /** The controls stay inert until a scale has actually been settled on. */
  const zoomReady = hasMeasured && scale > 0;

  const atMaximumZoom = zoomReady && scale >= MAX_SCALE;
  const atMinimumZoom = zoomReady && scale <= MIN_SCALE;

  // A disabled control keeps its tooltip, so the tooltip is where the reason for the
  // disabling belongs. Saying "Zoom in" on a button that will not zoom in tells the
  // user nothing about why, which is the pattern ChatModelSelector and RegenerateButton
  // already avoid. The plain label is retained while the control is merely not ready,
  // since that state is transient and has no reason worth naming.
  const zoomInLabel = atMaximumZoom ? t("common.zoom.atMaximum") : t("common.zoom.in");
  const zoomOutLabel = atMinimumZoom ? t("common.zoom.atMinimum") : t("common.zoom.out");
  const fitToWidthLabel = isFitToWidth
    ? t("citations.pdfViewer.alreadyFitted")
    : t("citations.pdfViewer.fitToWidth");

  return (
    <div className="flex h-[75vh] w-full flex-col">
      <div ref={scrollRef} className="flex-1 overflow-auto bg-default-100 p-4">
        {/*
          `mx-auto w-fit` rather than `items-center`, which is what centres the pages
          while keeping a page wider than the container reachable. Centring by
          `align-items` puts the left overflow outside the scroll range, so the start
          of a wide page could not be scrolled to at all.
        */}
        <div ref={containerRef} className="mx-auto flex w-fit flex-col" />
      </div>

      <div
        className="flex shrink-0 items-center justify-between gap-2 border-t border-default-200 bg-content1 px-3 py-2"
        data-id="pdf-viewer-toolbar"
      >
        <span className="text-sm text-default-500" data-id="pdf-viewer-page-count">
          {numPages > 0 ? t("common.pageCount", { count: numPages }) : null}
        </span>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger>
              <Button
                isIconOnly
                aria-label={zoomOutLabel}
                data-id="pdf-viewer-zoom-out"
                isDisabled={!zoomReady || scale <= MIN_SCALE}
                size="sm"
                variant="ghost"
                onPress={handleZoomOut}
              >
                <Icon icon="solar:magnifer-zoom-out-linear" width={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{zoomOutLabel}</TooltipContent>
          </Tooltip>

          {/*
            The wording sits in visually hidden text rather than in an `aria-label`.
            A label replaces the name computed from the content, so the region
            announced "Zoom level" and never the percentage, which is the one thing
            it exists to report, and the accessible name no longer contained the
            visible text (WCAG 2.5.3 Label in Name).
          */}
          <span
            aria-live="polite"
            className="min-w-[4ch] select-none text-center text-sm tabular-nums text-default-500"
            data-id="pdf-viewer-zoom-level"
            role="status"
          >
            <span className="sr-only">{t("common.zoom.level")}</span>
            <span data-id="pdf-viewer-zoom-value">
              {zoomReady ? `${Math.round(scale * ZOOM_PERCENTAGE_MULTIPLIER)}%` : null}
            </span>
          </span>

          <Tooltip>
            <TooltipTrigger>
              <Button
                isIconOnly
                aria-label={zoomInLabel}
                data-id="pdf-viewer-zoom-in"
                isDisabled={!zoomReady || scale >= MAX_SCALE}
                size="sm"
                variant="ghost"
                onPress={handleZoomIn}
              >
                <Icon icon="solar:magnifer-zoom-in-linear" width={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{zoomInLabel}</TooltipContent>
          </Tooltip>

          <div className="mx-0.5 h-4 w-px bg-default-200" />

          <Tooltip>
            <TooltipTrigger>
              <Button
                isIconOnly
                aria-label={fitToWidthLabel}
                data-id="pdf-viewer-fit-to-width"
                isDisabled={isFitToWidth}
                size="sm"
                variant="ghost"
                onPress={handleFitToWidth}
              >
                <Icon icon="solar:align-horizontal-spacing-linear" width={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{fitToWidthLabel}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
